
import { defineComponent } from "vue";
import store from '@/store/index';
import qrcode from 'qrcode-generator';
import { fetchedAsset } from '@/_models/Token';
import TokenService from "@/services/TokenService";
import ResponseData from "@/_models/ResponseData";
import { Helper } from "@/utils/Helper";
import tokensConfirmCreateTokenModal from "@/modules/tokens/components/modals/tokensConfirmCreateTokenModal.vue";
import { MosaicHttp, AccountHttp, Address, AggregateTransaction, Convert, Deadline, MosaicDefinitionTransaction, Mosaic, MosaicId, MosaicMetadataTransaction, MosaicNonce, MosaicProperties, MosaicSupplyChangeTransaction, MosaicSupplyType, PublicAccount, UInt64 } from "tsjs-xpx-chain-sdk";
import { Peer } from "peerjs";
import OnboardingChecklistService from "@/services/OnboardingChecklistService";

export default defineComponent({
  name: 'tokensCreateMainnetAssetComponent',
  props: {
    tokenName: String,
    symbol: String,
    tokenType: String,
    supply: Number,
    tokenImage: String,
    testnetAssetId: String,
  },
  components: {
    tokensConfirmCreateTokenModal,
  },

  data: function () {
    return {
      Helper: Helper,
      qr: '',
      isQRModalShow: false,
      userData: store.getters['auth/user'] ?? "",
      jwtToken: store.getters['auth/jwtToken'] ?? "",
      mainnetJwtToken: store.getters['auth/mainnetJwtToken'] ?? "",
      isDisabledModalConfirmSubmitButton: false,
      showUploadingLoader: false,
      isInsufficientBalance: false,
      isInvalidPublicKey: false,
      mainnetTokenData: '',
      mosaicNonce: '',
      assetId: '0',
    }
  },

  methods: {
    async createMainnetTokenQr() {
      if (this.userData !== "" && this.jwtToken !== "") {
        const publicAcc = PublicAccount.createFromPublicKey(this.publicKey, store.getters['config/getMainnetNetworkType']);
        let mosaicTxn :MosaicDefinitionTransaction;
        let mosaicSupplyTxn :MosaicSupplyChangeTransaction;

        let xarcadeHex = Convert.utf8ToHex('xarcade');
        let xarcadeHexData = xarcadeHex + "00".repeat((16-xarcadeHex.length)/2)

        let xarcadeValue = {
          name:this.tokenName,
          symbol:this.symbol,
          type:this.tokenType
        }

        let xarcadeMetadataTxn = MosaicMetadataTransaction.create(
          Deadline.create(),
          publicAcc,
          new MosaicId(this.assetId),
          UInt64.fromHex(xarcadeHexData),
          JSON.stringify(xarcadeValue),
          '',
          store.getters['config/getMainnetNetworkType']
        )
        if(this.tokenType=='IGT'){
          // fetch divisibility from testnet token
          let mosaicHttpTestnet = new MosaicHttp(store.getters['config/getTestnetApi']);
          const mosaicId = new MosaicId(this.testnetAssetId);
          const assetTestnet = await mosaicHttpTestnet.getMosaic(mosaicId).toPromise();
          mosaicTxn= MosaicDefinitionTransaction.create(
            Deadline.create(),
            this.mosaicNonce,
            new MosaicId(this.assetId),
            MosaicProperties.create({
              supplyMutable: true,
              transferable: true,
              divisibility: assetTestnet.divisibility,
              duration: undefined
            }),
            store.getters['config/getMainnetNetworkType']
          )
          mosaicSupplyTxn = MosaicSupplyChangeTransaction.create(
            Deadline.create(),
            new MosaicId(this.assetId),
            MosaicSupplyType.Increase,
            UInt64.fromUint(Math.trunc(this.supply * Math.pow(10, 4) )),
            store.getters['config/getMainnetNetworkType']
          )
        }else if(this.tokenType =='LET'){
          mosaicTxn= MosaicDefinitionTransaction.create(
            Deadline.create(),
            this.mosaicNonce,
            new MosaicId(this.assetId),
            MosaicProperties.create({
              supplyMutable: false,
              transferable: true,
              divisibility: 0,
              duration: undefined
            }),
            store.getters['config/getMainnetNetworkType']
          )
          mosaicSupplyTxn = MosaicSupplyChangeTransaction.create(
            Deadline.create(),
            new MosaicId(this.assetId),
            MosaicSupplyType.Increase,
            UInt64.fromUint(Math.trunc(this.supply)),
            store.getters['config/getMainnetNetworkType']
          )
        }else if(this.tokenType =='NFT'){
           mosaicTxn= MosaicDefinitionTransaction.create(
            Deadline.create(),
            this.mosaicNonce,
            new MosaicId(this.assetId),
            MosaicProperties.create({
              supplyMutable: false,
              transferable: true,
              divisibility: 0,
              duration: undefined
            }),
            store.getters['config/getMainnetNetworkType']
          )
          mosaicSupplyTxn = MosaicSupplyChangeTransaction.create(
            Deadline.create(),
            new MosaicId(this.assetId),
            MosaicSupplyType.Increase,
            UInt64.fromUint(1),
            store.getters['config/getMainnetNetworkType']
          )
        }
        let innerTxn = [
          mosaicTxn.toAggregate(publicAcc),
          mosaicSupplyTxn.toAggregate(publicAcc),
          xarcadeMetadataTxn.toAggregate(publicAcc),
        ]
        let aggregateTxn :AggregateTransaction
        if(this.isMultisig){
          aggregateTxn = AggregateTransaction.createBonded(
            Deadline.create(),
            innerTxn,
            store.getters['config/getMainnetNetworkType'],
            []
          )
        }else{
          aggregateTxn = AggregateTransaction.createComplete(
            Deadline.create(),
            innerTxn,
            store.getters['config/getMainnetNetworkType'],
            []
          )
        }

        const peer = new Peer();
        peer.on("open", (id) => {
          const qrCode = qrcode(0, 'H');
          let data = {
            payload:aggregateTxn.serialize(),
            type:'sign',
            recvId: peer.id,
            generationHash:this.$store.getters['config/getMainnetGenerationHash'],
            callbackUrl: null
          }
          qrCode.addData(JSON.stringify(data));
          qrCode.make();
          this.qr = qrCode.createSvgTag({scalable: true})
        });

        peer.on("connection", (conn) => {
          // console.log("connected to " + conn.peer);
          conn.on("data", (data: any) => {
            conn.send("success");
          });
        });
        this.isQRModalShow = true;
      }
    },

    async initAssetId() {
      if(this.userData.mainnetPublicAddress){
        let accountHttp = new AccountHttp(store.getters['config/getMainnetApi']);
        this.publicKey = this.userData.mainnetPublicAddress;

        if(!this.publicKey){
          this.isInvalidPublicKey = true;
        }
        // get account balance
        if(!this.isInvalidPublicKey){
          const address = Address.createFromRawAddress(this.userData.mainnetWalletAddress);
          let account = await accountHttp.getAccountInfo(address).toPromise();
          if(account.mosaics.length > 0){
            let assets:fetchedAsset[] = account.mosaics.map((asset:Mosaic) => {
              return {
                id: asset.id.toHex(),
                balance: asset.amount.compact(),
              }
            });
            let xpxAsset = assets.find(asset => asset.id.toLocaleUpperCase() == store.getters['config/getMainnetXPXAssetId']);
            if(xpxAsset.balance / Math.pow(10, 6) > 1500){
              // enough balance to create asset
              this.isInsufficientBalance = false;
            }else{
              // display insufficient balance and disable create token button
              this.isInsufficientBalance = true;
            }
          }
        }

        let nonce = MosaicNonce.createRandom();
        this.mosaicNonce = nonce;
        this.assetId = MosaicId.createFromNonce(this.mosaicNonce, PublicAccount.createFromPublicKey(this.publicKey, store.getters['config/getMainnetNetworkType'])).id.toHex();

        let multisigInfo = await accountHttp.getMultisigAccountInfo(Address.createFromRawAddress(this.userData.mainnetWalletAddress)).toPromise()
        this.isMultisig = multisigInfo.cosignatories.length>0?true:false;
      }
    },

    createMainnetToken(){
      if (this.userData !== "" && this.jwtToken !== "") {
        this.isDisabledModalConfirmSubmitButton = true;
        this.showUploadingLoader = true;
        let data = new FormData();
        data.append("userId", this.userData.userId);
        data.append("assetId", this.assetId);

        if (this.tokenName !== "") {
          data.append("title", this.tokenName);
          data.append("symbol", this.symbol);
          data.append("type", this.tokenType);

          if (this.tokenImage !== "") {
            data.append("logoStream", this.tokenImage);
          }

          TokenService.createToken(data, this.mainnetJwtToken, 'mainnet')
            .then(async(response: ResponseData) => {
              this.isQRModalShow = false;
              this.showUploadingLoader = false;
              this.isDisabledModalConfirmSubmitButton = false;
              Helper.toastSuccessHandling(response.data['message']);
              this.updateOnboardingChecklist(this.userData.userId, this.jwtToken);
              // this.$router.push("/tokens");
              // console.log('fetch mainnet token detail')
              await this.getMainnetTokenDetails(false);
            })
            .catch((error) => {
              this.isDisabledModalConfirmSubmitButton = false;
              this.showUploadingLoader = false;
              Helper.endpointErrorHandling(error);
            });
        } else {
          this.isDisabledModalConfirmSubmitButton = false;
          this.showUploadingLoader = false;
          Helper.endpointErrorHandling('', 'Token name is empty!');
        }
      }
    },

    updateOnboardingChecklist(userId: string, token: string) {
      OnboardingChecklistService.getOnboardingChecklist(userId, token)
        .then((response: ResponseData) => {
          OnboardingChecklistService.saveUpdatedOnboardingChecklist(response.data.items, 10, userId, token);
        })
        .catch((error) => {
          Helper.endpointErrorHandling(error);
        });
    },

    async getMainnetTokenDetails(isDefault: boolean){
      let tokenId = isDefault?this.$route.params.mainnetTokenId.toString():this.assetId;

      if (tokenId && this.userData !== "" && this.userData.mainnetWalletAddress) {
        await TokenService.getToken(tokenId, this.userData.userId, 'mainnet')
          .then((response: ResponseData) => {
            this.mainnetTokenData = {...response.data, formattedAmount: Helper.convertToCurrency(response.data.amount)};
          })
          .catch((error) => {
            Helper.endpointErrorHandling(error, 'Fetching of Token Details failed!');
          });
      }
    }
  },

  computed:{
    disableCreate(){
      if(!this.isInsufficientBalance){
        return false;
      }else{
        return true;
      }
    },
    typeLabel(){
      if(this.tokenType == 'IGT'){
        return 'In-Game Token';
      }else if(this.tokenType == 'LET'){
        return 'Limited Edition Token';
      }else{
        return 'Non-Fungible Token';
      }
    },
    mainnetLabel() {
      return store.getters['config/configMainnetLabel'];
    },
    assetUrl() {
      return store.getters['config/assetUrl'];
    },
    // mainnetTokenId() {
    //   return this.$route.params.mainnetTokenId.toString();
    // }
  },

  mounted() {
    console.log(this.$route.params.mainnetTokenId);
    this.getMainnetTokenDetails(true);
    this.initAssetId();
  },
});
